/**
 * ChatMessageInput Component
 * Multi-line text input with image attachment support
 * Supports Shift+Enter for newlines, Enter to send
 * Theme-aware with Tailwind dark: prefix classes
 */

import { useState, useRef, useEffect } from 'react';
import { ChatMessageInputProps, IImageAttachment } from '../../types/liveChat';
import { cn } from '../../utils/cn';
import { Send, Image as ImageIcon, X } from 'lucide-react';
import { liveChatAPI } from '../../services/api/liveChat';

export const ChatMessageInput: React.FC<Omit<ChatMessageInputProps, 'theme'>> = ({
  value,
  onChange,
  onSend,
  disabled = false,
  isLoading = false
}) => {
  const [imagePreview, setImagePreview] = useState<IImageAttachment | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Auto-resize textarea based on content
   */
  useEffect(() => {
    if (!textareaRef.current) return;

    textareaRef.current.style.height = 'auto';
    const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
    textareaRef.current.style.height = `${newHeight}px`;
  }, [value]);

  /**
   * Handle input change
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  /**
   * Handle key down (Shift+Enter = newline, Enter = send)
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * Send message with optional image
   */
  const handleSendMessage = () => {
    if (!value.trim() && !imagePreview) return;

    onSend(value, imagePreview || undefined);
    onChange('');
    setImagePreview(null);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  /**
   * Handle image selection
   */
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsProcessing(true);

      // Convert image to base64
      const processedImage = await liveChatAPI.uploadImage(file);
      setImagePreview(processedImage);
    } catch (error: any) {
      console.error('Image processing error:', error);
      alert(error.message || 'Failed to process image');
    } finally {
      setIsProcessing(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  /**
   * Remove image preview
   */
  const removeImagePreview = () => {
    setImagePreview(null);
  };

  return (
    <div className="border-t px-4 py-3 space-y-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      {/* Image preview */}
      {imagePreview && (
        <div className="relative inline-block">
          <img
            src={imagePreview.base64Data}
            alt="Preview"
            className="h-16 w-16 sm:h-20 sm:w-20 object-cover rounded"
          />
          <button
            onClick={removeImagePreview}
            className="absolute -top-2 -right-2 bg-error-500 text-white rounded-full p-1 hover:bg-error-600"
            type="button"
            aria-label="Remove image preview"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="flex gap-1 sm:gap-2 items-end min-w-0">
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={disabled || isLoading || isProcessing}
          className={cn(
            'flex-1 p-2 rounded-lg resize-none outline-none focus:ring-2 focus:ring-primary-500',
            'text-xs sm:text-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-primary-500 dark:focus:ring-primary-600',
            'min-w-0',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          rows={1}
        />

        {/* Image button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isLoading || isProcessing || !!imagePreview}
          className={cn(
            'p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 flex-shrink-0',
            disabled && 'cursor-not-allowed'
          )}
          type="button"
          title="Attach image"
          aria-label="Attach image"
        >
          <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
        </button>

        {/* Send button */}
        <button
          onClick={handleSendMessage}
          disabled={disabled || isLoading || isProcessing || (!value.trim() && !imagePreview)}
          className={cn(
            'p-2 rounded-lg transition-colors flex-shrink-0',
            !disabled && (value.trim() || imagePreview)
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
          )}
          type="button"
          title="Send message"
          aria-label="Send message"
        >
          <Send className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
        />
      </div>

      {/* Character count hint - Hidden on mobile to save space */}
      <div className="hidden sm:block text-xs text-gray-400 dark:text-gray-500">
        {value.length > 0 && `${value.length} characters`}
      </div>
    </div>
  );
};